import React, { VideoHTMLAttributes } from 'react';
import styled from 'styled-components';
import {
  themeVal,
  glsp,
  media,
  multiply,
  ThemeValPath
} from '@devseed-ui/theme-provider';

import { ErrorBlock } from './error';
import Constrainer from '$styles/constrainer';
import MediaComponent, { MediaItem, MediaVideo } from '$components/media';
import { getAttributionObj } from './utils';

const constrainerPadding = (size: string) =>
  glsp(multiply(themeVal(`layout.gap.${size}` as ThemeValPath), 2) as number);

const FluidConstrainer = styled(Constrainer)`
  width: auto;
`;

const ImageBlockMedia = styled(MediaComponent)`
  ${MediaItem} {
    display: flex;
    justify-content: center;

    .gatsby-image-wrapper {
      max-width: 100% !important;
      width: calc(100vw - ${constrainerPadding('xsmall')});

      ${media.smallUp`
        width: calc(100vw - ${constrainerPadding('small')});
      `}

      ${media.mediumUp`
        width: calc(100vw - ${constrainerPadding('medium')});
      `}

      ${media.largeUp`
        width: calc(100vw - ${constrainerPadding('large')});
      `}

      ${media.xlargeUp`
        width: calc(100vw - ${constrainerPadding('xlarge')});
      `}
    }
  }
`;

const htmlOrChildren = (value: string | React.ReactNode) =>
  typeof value === 'string'
    ? { dangerouslySetInnerHTML: { __html: value } }
    : { children: value };

// // // // // // // // // // // // // // // // // // // // // // // // // // //
// Block type: image
// // // // // // // // // // // // // // // // // // // // // // // // // // //

interface BlockImageProps {
  size?: 'large' | 'cover' | 'default';
  decoration?: 'left' | 'right' | 'top' | 'bottom' | 'none';
  url: string;
  attribution?: {
    name: string;
    url?: string;
  };
  caption: string | React.ReactNode;
}

const BlockImage = ({
  size,
  decoration,
  url,
  attribution,
  caption
}: BlockImageProps) => {
  const ImageBlockMediaEl =
    !size || size === 'default' ? ImageBlockMedia : MediaComponent;

  const content = (
    <ImageBlockMediaEl
      size={size}
      decoration={decoration}
      src={url}
      alt='Image'
      attribution={attribution}
      {...htmlOrChildren(caption)}
    />
  );
  return !size || size === 'default' ? (
    <FluidConstrainer>{content}</FluidConstrainer>
  ) : (
    content
  );
};

// // // // // // // // // // // // // // // // // // // // // // // // // // //
// Block type: video
// // // // // // // // // // // // // // // // // // // // // // // // // // //

interface BlockVideoProps {
  size?: 'large' | 'default' | 'cover';
  url: string;
  attribution?: {
    name: string;
    url?: string;
  };
  caption: string | React.ReactNode;
  videoProps?: VideoHTMLAttributes<HTMLVideoElement>;
}

const BlockVideo = ({
  size,
  url,
  attribution,
  caption,
  videoProps
}: BlockVideoProps) => {
  const content = (
    <MediaVideo
      size={size}
      src={url}
      attribution={attribution}
      videoProps={videoProps}
      {...htmlOrChildren(caption)}
    />
  );
  return !size || size === 'default' ? (
    <FluidConstrainer>{content}</FluidConstrainer>
  ) : (
    content
  );
};

interface MediaProps
  extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'src'> {
  size?: 'large' | 'cover' | 'default';
  decoration?: 'left' | 'right' | 'top' | 'bottom' | 'none';
  src: string | { default: string };
  attributionName?: string;
  attributionUrl?: string;
  children?: React.ReactNode;
}

export function Media(props: MediaProps) {
  const { size = 'default', decoration, src, children, ...rest } = props;

  const imgSrc = typeof src === 'string' ? src : src.default;

  const isVideo = !!imgSrc.toLowerCase().match(/\.(mp4|webm)$/);
  const isImage = !!imgSrc.toLowerCase().match(/\.(jpeg|jpg|gif|png)$/);

  if (!isVideo && !isImage) {
    return (
      <ErrorBlock>
        <p>Invalid media file type</p>
        <p>
          Supported video types: <code>mp4</code>, <code>webm</code>
        </p>
        <p>
          Supported image types: <code>jpg</code>, <code>gif</code>,{' '}
          <code>png</code>
        </p>
      </ErrorBlock>
    );
  }

  if (isVideo) {
    const errorContent = !['large', 'cover', 'default'].includes(size) ? (
      <p>
        When using a video, the <strong>Media</strong> block only supports{' '}
        <strong>large</strong>, or <strong>default</strong> sizes.
      </p>
    ) : decoration ? (
      <p>
        When using a video, the <strong>Media</strong> block does not support
        decoration.
      </p>
    ) : null;

    return errorContent ? (
      <ErrorBlock>{errorContent}</ErrorBlock>
    ) : (
      <BlockVideo
        size={size}
        url={imgSrc}
        attribution={getAttributionObj(props)}
        videoProps={rest}
        caption={children}
      />
    );
  }

  return (
    <BlockImage
      size={size}
      url={imgSrc}
      attribution={getAttributionObj(props)}
      caption={children}
      decoration={decoration}
    />
  );
}
